// src/components/CustomerSettings.js
import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Divider,
  Alert,
  Switch,
  FormControlLabel,
  Grid,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Save,
  Visibility,
  VisibilityOff,
  Notifications,
  Security,
  Person,
  Business,
} from "@mui/icons-material";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

function CustomerSettings() {
  const { customer, customerData, refreshCustomerData } = useCustomerAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [accountData, setAccountData] = useState({
    name: customer?.name || "",
    email: customer?.contactEmail || customer?.portalEmail || customer?.email || "",
    phone: customer?.contactPhone || customer?.phone || "",
    address: customer?.address || "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: customer?.emailNotifications || false,
    smsNotifications: customer?.smsNotifications || false,
    jobUpdates: customer?.jobUpdates || false,
    invoiceReminders: customer?.invoiceReminders || false,
  });

  const handleAccountUpdate = async () => {
    setLoading(true);
    setMessage(null);
    try {
      await updateDoc(doc(db, "customers", customer.id), accountData);
      await refreshCustomerData();
      setMessage({ type: "success", text: "Account information updated!" });
    } catch (error) {
      console.error("Error updating account:", error);
      setMessage({ type: "error", text: "Failed to update account information." });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setLoading(true);
    setMessage(null);

    try {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error("Passwords do not match!");
      }

      if (passwordData.newPassword.length < 6) {
        throw new Error("Password must be at least 6 characters!");
      }

      // TODO: In production, verify currentPassword first
      await updateDoc(doc(db, "customers", customer.id), {
        portalPassword: passwordData.newPassword,
      });

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setMessage({ type: "success", text: "Password changed successfully!" });
    } catch (error) {
      console.error("Error changing password:", error);
      setMessage({ type: "error", text: error.message || "Failed to change password." });
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationUpdate = async () => {
    setLoading(true);
    setMessage(null);

    try {
      await updateDoc(doc(db, "customers", customer.id), notificationSettings);
      await refreshCustomerData();
      setMessage({ type: "success", text: "Notification preferences updated!" });
    } catch (error) {
      console.error("Error updating notifications:", error);
      setMessage({ type: "error", text: "Failed to update notification preferences." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your account and preferences
        </Typography>
      </Box>

      {/* Success/Error Message */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Account Information */}
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <Person color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Account Information
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Company Name"
                  fullWidth
                  value={accountData.name}
                  onChange={(e) => setAccountData({ ...accountData, name: e.target.value })}
                  disabled={loading}
                  InputLabelProps={{
                    sx: { color: "white" }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email Address"
                  type="email"
                  fullWidth
                  value={accountData.email}
                  onChange={(e) => setAccountData({ ...accountData, email: e.target.value })}
                  disabled={loading}
                  InputLabelProps={{
                    sx: { color: "white" }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  fullWidth
                  value={accountData.phone}
                  onChange={(e) => setAccountData({ ...accountData, phone: e.target.value })}
                  disabled={loading}
                  InputLabelProps={{
                    sx: { color: "white" }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  fullWidth
                  multiline
                  rows={3}
                  value={accountData.address}
                  onChange={(e) => setAccountData({ ...accountData, address: e.target.value })}
                  disabled={loading}
                  InputLabelProps={{
                    sx: { color: "white" }
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleAccountUpdate}
                disabled={loading}
                sx={{
                  background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #d97706 0%, #dc2626 100%)",
                  },
                }}
              >
                Save Changes
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <Security color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Change Password
              </Typography>
            </Box>

            <Stack spacing={2}>
              <TextField
                label="Current Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                disabled={loading}
                InputLabelProps={{
                  sx: { color: "white" }
                }}
              />
              <TextField
                label="New Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                disabled={loading}
                helperText="Minimum 6 characters"
                InputLabelProps={{
                  sx: { color: "white" }
                }}
              />
              <TextField
                label="Confirm New Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                disabled={loading}
                InputLabelProps={{
                  sx: { color: "white" }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<Security />}
                onClick={handlePasswordChange}
                disabled={loading || !passwordData.currentPassword || !passwordData.newPassword}
              >
                Change Password
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <Notifications color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Notification Preferences
              </Typography>
            </Box>

            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.emailNotifications}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      emailNotifications: e.target.checked
                    })}
                    disabled={loading}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Email Notifications
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Receive updates via email
                    </Typography>
                  </Box>
                }
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.smsNotifications}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      smsNotifications: e.target.checked
                    })}
                    disabled={loading}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      SMS Notifications
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Receive text message updates
                    </Typography>
                  </Box>
                }
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.jobUpdates}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      jobUpdates: e.target.checked
                    })}
                    disabled={loading}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Job Status Updates
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Get notified when job status changes
                    </Typography>
                  </Box>
                }
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.invoiceReminders}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      invoiceReminders: e.target.checked
                    })}
                    disabled={loading}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Invoice Reminders
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Reminders for unpaid invoices
                    </Typography>
                  </Box>
                }
              />
            </Stack>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleNotificationUpdate}
                disabled={loading}
                sx={{
                  background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #d97706 0%, #dc2626 100%)",
                  },
                }}
              >
                Save Preferences
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card sx={{ bgcolor: "rgba(59,130,246,0.05)" }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              ℹ️ Account Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Portal Email: {customerData?.portalEmail || customer?.email}<br />
              Account Created: {customerData?.portalCreatedAt ? 
                new Date(customerData.portalCreatedAt.seconds * 1000).toLocaleDateString() : "N/A"}
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

export default CustomerSettings;
